import { createDualRange } from "./dual-range.js";
import { createCurvePicker } from "./curve-picker.js";

const AXES = [
  { key: "action",      label: "Action",      tag: "x-ring", colorVar: "--c-suit-action" },
  { key: "observation", label: "Observation", tag: "y-ring", colorVar: "--c-suit-observation" },
  { key: "relation",    label: "Relation",    tag: "z-ring", colorVar: "--c-suit-relation" },
];

export function mountPanel(rootEl, sim, mode = "simple") {
  rootEl.innerHTML = `<div class="axis-list"></div>`;
  const list = rootEl.querySelector(".axis-list");
  for (const cfg of AXES) {
    const m = sim.magnets[cfg.key];
    const card = mode === "advanced" ? renderAdvancedCard(cfg, m) : renderSimpleCard(cfg, m);
    list.appendChild(card);
  }
}

function renderSimpleCard(cfg, m) {
  const card = document.createElement("section");
  card.className = "axis";
  card.dataset.key = cfg.key;
  card.style.setProperty("--axis-color", `var(${cfg.colorVar})`);
  card.innerHTML = `
    <header class="axis-head">
      <span class="axis-label">${cfg.label}</span>
      <span class="axis-tag">${cfg.tag}</span>
    </header>
    <label>Speed <output data-out="speed">${m.speed.high.toFixed(2)}</output>
      <span class="slider bipolar">
        <input type="range" min="-1.5" max="1.5" step="0.05" value="${m.speed.high}" data-control="speed">
      </span>
    </label>
    <label>Force <output data-out="force">${m.force.high.toFixed(0)}</output>
      <span class="slider bipolar">
        <input type="range" min="-300" max="300" step="5" value="${m.force.high}" data-control="force">
      </span>
    </label>
    <label class="osc">
      <input type="checkbox" data-control="osc" ${m.speed.osc || m.force.osc ? "checked" : ""}>
      Oscillate ∿ both
    </label>
  `;

  card.querySelector('[data-control="speed"]').addEventListener("input", (e) => {
    const v = parseFloat(e.target.value);
    m.speed.high = v;
    m.speed.low = -v;
    card.querySelector('[data-out="speed"]').textContent = v.toFixed(2);
  });
  card.querySelector('[data-control="force"]').addEventListener("input", (e) => {
    const v = parseFloat(e.target.value);
    m.force.high = v;
    m.force.low = -v;
    card.querySelector('[data-out="force"]').textContent = v.toFixed(0);
  });
  card.querySelector('[data-control="osc"]').addEventListener("change", (e) => {
    m.speed.osc = e.target.checked;
    m.force.osc = e.target.checked;
  });

  return card;
}

function renderAdvancedCard(cfg, m) {
  const card = document.createElement("section");
  card.className = "axis";
  card.dataset.key = cfg.key;
  card.style.setProperty("--axis-color", `var(${cfg.colorVar})`);
  card.innerHTML = `
    <header class="axis-head">
      <span class="axis-label">${cfg.label}</span>
      <span class="axis-tag">${cfg.tag}</span>
    </header>
  `;

  card.appendChild(buildParamRow(cfg, m, "speed", { min: -1.5, max: 1.5, step: 0.05, fmt: (v) => v.toFixed(2) }));
  card.appendChild(buildParamRow(cfg, m, "force", { min: -300, max: 300, step: 5,   fmt: (v) => v.toFixed(0) }));

  return card;
}

function buildParamRow(cfg, m, paramKey, { min, max, step, fmt }) {
  const param = m[paramKey];
  const row = document.createElement("div");
  row.className = "param-row";

  // Line 1: label + low num + dual range + high num
  const line1 = document.createElement("div");
  line1.className = "param-line";

  const label = document.createElement("span");
  label.className = "param-label";
  label.textContent = paramKey === "speed" ? "Speed" : "Force";

  const lowInput  = makeNumInput(min, max, step, param.low);
  const highInput = makeNumInput(min, max, step, param.high);

  const range = createDualRange({
    min, max, step,
    low: param.low,
    high: param.high,
    onChange: ({ low, high }) => {
      param.low = low;
      param.high = high;
      lowInput.value  = fmt(low);
      highInput.value = fmt(high);
    },
  });
  range.inputs.lo.dataset.control = `${paramKey}-lo`;
  range.inputs.hi.dataset.control = `${paramKey}-hi`;

  lowInput.addEventListener("input", () => {
    const v = clamp(parseFloat(lowInput.value) || 0, min, max);
    if (v > param.high) { lowInput.value = fmt(param.high); return; }
    param.low = v;
    range.setLow(v);
  });
  highInput.addEventListener("input", () => {
    const v = clamp(parseFloat(highInput.value) || 0, min, max);
    if (v < param.low) { highInput.value = fmt(param.low); return; }
    param.high = v;
    range.setHigh(v);
  });

  line1.append(label, lowInput, range.el, highInput);

  // Line 2: osc pill + curve picker
  const line2 = document.createElement("div");
  line2.className = "param-line param-line-controls";

  const oscBtn = document.createElement("button");
  oscBtn.type = "button";
  oscBtn.className = "ts-btn osc-pill";
  oscBtn.dataset.osc = paramKey;
  oscBtn.setAttribute("aria-pressed", String(param.osc));
  oscBtn.textContent = param.osc ? "∿ on" : "∿ off";
  oscBtn.addEventListener("click", () => {
    param.osc = !param.osc;
    oscBtn.setAttribute("aria-pressed", String(param.osc));
    oscBtn.textContent = param.osc ? "∿ on" : "∿ off";
  });

  const picker = createCurvePicker({
    value: param.curve,
    onChange: (curve) => { param.curve = curve; },
  });

  line2.append(oscBtn, picker.el);

  row.append(line1, line2);
  return row;
}

function makeNumInput(min, max, step, value) {
  const el = document.createElement("input");
  el.type = "number";
  el.className = "num";
  el.min = String(min);
  el.max = String(max);
  el.step = String(step);
  el.value = String(value);
  // Disable wheel-to-change so scrolling the page doesn't nudge values.
  el.addEventListener("wheel", (e) => e.preventDefault(), { passive: false });
  return el;
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
